// SPDX-FileCopyrightText: 2026 TRUMPF Laser SE
//
// SPDX-License-Identifier: LicenseRef-TRUMPF
import type { ComponentProps } from 'react';
import type { ScrollView } from '@tls/treact-ui';
import { createGlobalStyle } from 'styled-components';
import { nativeTableHeaderStyles, nativeTableRowStyles, nativeVerticalScrollStyles } from './nativeScrollStyles';

const nativeContentAttribute = 'data-native-scroll-content';
const nativeScrollClass = 'sw91-native-scroll';

// Tables, ThreeSectorContent and LicenseComponent share these ScrollView props.
// `bottom` also prevents the table's 4rem header spacer.
// Mark the original viewport so CSS can make it content of the native outer one.
export const nativeScrollViewProps = {
  appearance: 'bottom',
  className: nativeScrollClass,
  scrollContainerRef: (node: HTMLDivElement | null) => {
    node?.setAttribute(nativeContentAttribute, '');
  },
} satisfies ComponentProps<typeof ScrollView>;

// Compatibility selectors for @tls/treact-ui 0.510.1. Scope to Headless UI popup
// roots, including portals. No generated styled-components class names.
// Standard: popup > inner > ScrollView (DropdownContent/Header, Combobox, FunctionMenu).
// Multiple: inner contains select-all, divider, then ScrollView.
// Filter: inner > section contains search, then item container > ScrollView.
// In appearance=small the first two children are indicator masks; the third is
// the original viewport, even for empty lists. Remaining siblings are scroll buttons.
const popup = 'ul:is([role="listbox"], [role="menu"])[id^="headlessui-"]';
const popupScrollRoots = `:is(
  ${popup} > div > div:only-child,
  ${popup}[aria-multiselectable="true"] > div > div:nth-child(3),
  ${popup} > div > section > div:last-child > div:only-child
)`;

export const TreactNativeScrollStyles = createGlobalStyle`
  .${nativeScrollClass} > :not([${nativeContentAttribute}]),
  ${popupScrollRoots} > :not(:nth-child(3)) {
    display: none !important;
  }

  .${nativeScrollClass},
  ${popupScrollRoots} {
    ${nativeVerticalScrollStyles};
    display: block;
    overflow-x: hidden;
    overscroll-behavior-y: contain;
  }

  /* Keep treact's content/ref hierarchy, but scroll its unstyled outer container.
     Resetting WebKit pseudo-elements on the old viewport does not restore native
     painting in Chromium. With visible overflow it paints no scrollbar at all. */
  .${nativeScrollClass} > [${nativeContentAttribute}],
  ${popupScrollRoots} > div:nth-child(3) {
    overflow: visible !important;
    height: auto;
    /* Remove treact's Firefox compensation for the wide custom track. */
    margin-right: 0 !important;
    padding-right: 0 !important;
  }

  [role='table']:has(> [role='rowgroup'] > .${nativeScrollClass}) {
    > [role='rowgroup']:not(:has(> .${nativeScrollClass})) {
      ${nativeTableHeaderStyles};
    }

    > [role='rowgroup']:has(> .${nativeScrollClass}) {
      min-height: 0;
      ${nativeTableRowStyles};

      > .${nativeScrollClass} {
        scrollbar-gutter: stable;
      }
    }
  }
`;
